import {FC} from 'react';
import cn from 'classnames';

import {Button} from '@zoefin/design-system';
import {logger} from '@zoefin/logger';

import {categoryTierFor} from '@/modules/FinancialHealthScore/scoring';
import {ScoreResult} from '@/modules/FinancialHealthScore/scoring/types';

import {BADGE_LABEL} from '../../../../constants';
import {downloadPdf} from '../../utils';

import styles from './styles.module.scss';

// Small helper component for the score breakdown badges.
interface CategoryBadgeProps {
  score: number;
}

const CategoryBadge: FC<CategoryBadgeProps> = ({score}) => {
  const tier = categoryTierFor(score);
  const badgeClassName = cn(styles.badge, {
    [styles.badge__modLookingGood]: tier === 'looking-good',
    [styles.badge__modWorthLook]: tier === 'worth-a-look',
    [styles.badge__modNeedsAttention]: tier === 'needs-attention',
  });

  return <span className={badgeClassName}>{BADGE_LABEL[tier]}</span>;
};

interface ScoreBreakdownProps {
  result: ScoreResult;
  firstName?: string;
}

const ScoreBreakdown: FC<ScoreBreakdownProps> = ({result, firstName}) => {
  const handleDownload = (): void => {
    downloadPdf({firstName, result}).catch((error) =>
      logger.error({error}, 'FHS: failed to download PDF report'),
    );
  };

  return (
    <div className={styles.scoreBreakdown__breakdown}>
      <div className={styles.scoreBreakdown__breakdownHeader}>
        <h3 className={styles.scoreBreakdown__breakdownTitle}>
          Score Breakdown
        </h3>
        <Button
          as="button"
          size="medium"
          onClick={handleDownload}
          className={styles.scoreBreakdown__downloadButton}
          withIcon={{
            iconSrc: 'fa-light fa-download',
            isFontAwesome: true,
          }}>
          Download Your Report
        </Button>
      </div>
      <p className={styles.scoreBreakdown__breakdownDescription}>
        Your score reflects six areas of your financial life.{' '}
        <strong>&ldquo;Needs attention&rdquo;</strong> reveals your biggest
        gaps, &ldquo;<strong>Worth a look</strong>&rdquo; suggests areas that
        could use a little help, and &ldquo;<strong>Looking good</strong>&rdquo;
        shows where you&apos;re doing well.
      </p>
      <ul className={styles.scoreBreakdown__rows}>
        {result.breakdown.map((row) => (
          <li key={row.key} className={styles.scoreBreakdown__row}>
            <span className={styles.scoreBreakdown__rowLabel}>{row.label}</span>
            <CategoryBadge score={row.rawScore} />
            <span className={styles.scoreBreakdown__rowScore}>
              {row.rawScore}/100
            </span>
          </li>
        ))}
      </ul>
    </div>
  );
};

export default ScoreBreakdown;
